import { situationsText } from './SpecialSituationsQuestion';
// Step 1 (İnceleyen): Temel bilgiler sadece görüntülenir; hatalı satır için doğru değer önerilebilir
import { useEffect, useState } from 'react';
import { Lock, Flag, Pencil, Trash2, ArrowDown } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Checkbox } from '../../ui/checkbox';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { WORK_PERMIT_TYPES } from '../constants';
import { INFO_FIELDS, displayValue, pickFields } from './infoCorrections';

const CorrectionEditor = ({ fieldKey, draft, setDraft, locations, workAreas, loadAreas, baseLocationId }) => {
  const set = (patch) => setDraft(prev => ({ ...prev, ...patch }));
  const loc = draft.location_id || baseLocationId;
  useEffect(() => { loadAreas?.(loc); }, [loc, loadAreas]);
  const tipis = [...new Set(workAreas.filter(w => w.location_id === loc).map(w => w.is_tipi))];
  const cesits = [...new Set(workAreas.filter(w => w.location_id === loc && w.is_tipi === draft.is_tipi).map(w => w.is_cesidi))];
  switch (fieldKey) {
    case 'location':
      return (
        <Select value={draft.location_id} onValueChange={(v) => set({ location_id: v })}>
          <SelectTrigger data-testid="correction-input-location"><SelectValue placeholder="Lokasyon seçin" /></SelectTrigger>
          <SelectContent>{locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
        </Select>
      );
    case 'job_type':
      return (
        <div className="grid grid-cols-2 gap-2">
          <Select value={draft.is_tipi} onValueChange={(v) => set({ is_tipi: v, is_cesidi: '' })}>
            <SelectTrigger data-testid="correction-input-is-tipi"><SelectValue placeholder="İş tipi" /></SelectTrigger>
            <SelectContent>{tipis.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={draft.is_cesidi} onValueChange={(v) => set({ is_cesidi: v })}>
            <SelectTrigger data-testid="correction-input-is-cesidi"><SelectValue placeholder="İş çeşidi" /></SelectTrigger>
            <SelectContent>{cesits.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      );
    case 'start':
    case 'end':
      return (
        <div className="grid grid-cols-[1fr_140px] gap-2">
          <Input type="date" value={draft[`${fieldKey}_date`]} onChange={(e) => set({ [`${fieldKey}_date`]: e.target.value })} data-testid={`correction-input-${fieldKey}-date`} />
          <Input type="time" value={draft[`${fieldKey}_time`]} onChange={(e) => set({ [`${fieldKey}_time`]: e.target.value })} data-testid={`correction-input-${fieldKey}-time`} />
        </div>
      );
    case 'job_description':
      return <Textarea rows={3} value={draft.job_description} onChange={(e) => set({ job_description: e.target.value })} data-testid="correction-input-job-description" />;
    case 'on_behalf_of':
      return <Input value={draft.on_behalf_of} onChange={(e) => set({ on_behalf_of: e.target.value })} placeholder="Boş bırakılırsa firma kendi adına" data-testid="correction-input-on-behalf-of" />;
    default:
      return (
        <div className="grid sm:grid-cols-2 gap-2">
          {WORK_PERMIT_TYPES.map(t => (
            <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={draft.work_permit_types.includes(t.id)}
                onCheckedChange={(c) => set({ work_permit_types: c ? [...draft.work_permit_types, t.id] : draft.work_permit_types.filter(x => x !== t.id) })}
                data-testid={`correction-input-type-${t.id}`}
              />{t.label}
            </label>
          ))}
        </div>
      );
  }
};

const Row = ({ fieldKey, label, value, testId, correction, onSave, onRemove, editorProps, formData }) => {
  const [draft, setDraft] = useState(null);
  const canReport = !!fieldKey && !!onSave;
  const open = () => setDraft(correction ? { ...correction } : pickFields(fieldKey, formData));
  return (
    <div className="py-2.5 border-b border-slate-100 last:border-0" data-testid={fieldKey ? `ro-row-${fieldKey}` : undefined}>
      <div className="grid grid-cols-[180px_1fr_auto] gap-4 items-start">
        <span className="text-sm text-slate-500">{label}</span>
        <span className={`text-sm font-medium break-words ${correction ? 'text-rose-600 line-through' : 'text-slate-800'}`} data-testid={testId}>{value || '-'}</span>
        {canReport && !draft && (correction ? (
          <div className="flex gap-1">
            <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={open} data-testid={`ro-edit-error-${fieldKey}`}><Pencil className="h-3.5 w-3.5 mr-1" />Düzenle</Button>
            <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-xs text-rose-600 hover:bg-rose-50" onClick={onRemove} data-testid={`ro-remove-error-${fieldKey}`}><Trash2 className="h-3.5 w-3.5" /></Button>
          </div>
        ) : (
          <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-xs border-rose-200 text-rose-600 hover:bg-rose-50" onClick={open} data-testid={`ro-report-error-${fieldKey}`}>
            <Flag className="h-3.5 w-3.5 mr-1" />Hatayı Bildir
          </Button>
        ))}
      </div>
      {correction && !draft && (
        <p className="mt-1.5 ml-[196px] text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2.5 py-1.5" data-testid={`ro-suggested-${fieldKey}`}>
          <span className="font-semibold">Önerilen: </span>{displayValue(fieldKey, correction, editorProps.locations)}
        </p>
      )}
      {draft && (
        <div className="mt-2 ml-[196px] space-y-2 rounded-lg border border-rose-200 bg-rose-50/40 p-3 animate-in fade-in slide-in-from-top-1" data-testid={`ro-editor-${fieldKey}`}>
          <p className="text-xs font-medium text-rose-700">Olması gereken {label.toLocaleLowerCase('tr-TR')} bilgisini girin</p>
          <CorrectionEditor fieldKey={fieldKey} draft={draft} setDraft={setDraft} {...editorProps} />
          <div className="flex justify-end gap-2">
            <Button type="button" size="sm" variant="ghost" onClick={() => setDraft(null)} data-testid={`ro-editor-cancel-${fieldKey}`}>Vazgeç</Button>
            <Button type="button" size="sm" className="bg-rose-600 hover:bg-rose-700" onClick={() => { onSave(draft); setDraft(null); }} data-testid={`ro-editor-save-${fieldKey}`}>Öneriyi Kaydet</Button>
          </div>
        </div>
      )}
    </div>
  );
};

const WizardStep1ReadOnly = ({ formData, locations, companies, workAreas = [], loadAreas, corrections, setCorrections, onGoToEnd }) => {
  const company = companies.find(c => c.id === formData.company_id)?.name;
  const canReport = !!setCorrections;
  const rowProps = (key) => ({
    fieldKey: key, label: INFO_FIELDS[key].label, formData, editorProps: { locations, workAreas, loadAreas, baseLocationId: corrections?.location?.location_id || formData.location_id },
    correction: corrections?.[key],
    onSave: canReport ? (v) => setCorrections(prev => ({ ...prev, [key]: v })) : null,
    onRemove: () => setCorrections(prev => { const n = { ...prev }; delete n[key]; return n; }),
  });
  const count = Object.keys(corrections || {}).length;
  return (
    <div className="max-w-4xl space-y-3" data-testid="wizard-step1-readonly">
      <p className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 border rounded-md px-3 py-2">
        <Lock className="h-3.5 w-3.5" />Bu bölüm firma tarafından doldurulur. Hatalı bir bilgi varsa satırın yanındaki "Hatayı Bildir" ile doğrusunu önerebilirsiniz.
      </p>
      <div className="rounded-lg border px-4">
        <Row {...rowProps('location')} value={displayValue('location', formData, locations)} testId="ro-location" />
        <Row label="Firma" value={company} testId="ro-company" />
        <Row {...rowProps('on_behalf_of')} value={formData.on_behalf_of || '-'} testId="ro-on-behalf-of" />
        <Row label="İş Ekipmanı" value={formData.has_equipment === false ? 'Yok' : formData.has_equipment ? `Var (${(formData.equipment_kinds || []).length} tür)${formData.equipment_company_name ? ` – Ekipman firması: ${formData.equipment_company_name}` : ''}` : '-'} testId="ro-equipment" />
        <Row {...rowProps('job_type')} value={displayValue('job_type', formData)} testId="ro-job-type" />
        <Row {...rowProps('start')} value={displayValue('start', formData)} testId="ro-start" />
        <Row {...rowProps('end')} value={displayValue('end', formData)} testId="ro-end" />
        <Row {...rowProps('job_description')} value={formData.job_description} testId="ro-description" />
        <Row {...rowProps('work_permit_types')} value={displayValue('work_permit_types', formData)} testId="ro-permit-types" />
        <Row label="Çalışmaya Bağlı Özel Durumlar" value={situationsText(formData.special_situations)} testId="ro-special-situations" />
      </div>
      {count > 0 && onGoToEnd && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3" data-testid="ro-errors-summary">
          <p className="text-sm text-rose-700"><span className="font-semibold">{count} alan</span> hatalı olarak işaretlendi. Talebi firmaya geri göndermek için talep sonuna gidin.</p>
          <Button type="button" size="sm" className="bg-rose-600 hover:bg-rose-700" onClick={onGoToEnd} data-testid="ro-go-to-end-btn">
            <ArrowDown className="h-4 w-4 mr-1" />Talep Sonuna Git
          </Button>
        </div>
      )}
    </div>
  );
};

export default WizardStep1ReadOnly;
